import { memo, useCallback, useEffect, useState } from "react";
import {
    Box,
    ButtonBase,
    Collapse,
    Divider,
    Typography,
    useTheme,
} from "@mui/material";
import { AltArrowDown, Logout2 } from "@solar-icons/react/ssr";

import {
    mobileNavDrawerId,
    mobileNavHeaderHeight,
    mobileNavTransitionDuration,
} from "@/nav/navConstants";
import { NavProvider, useNavContext } from "@/nav/NavContext";
import { getRouteBase } from "@/nav/navHooks";
import { NavItemIcon } from "@/nav/NavIcons";
import { getSideNavColors } from "@/nav/navTheme";
import OrganizationSwitcher from "@/nav/OrganizationSwitcher";
import ThemeSwitch from "@/nav/ThemeSwitch";
import { colorPalette } from "@/themes";

const getNavItemTestId = (title) =>
    `nav-item-${title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")}`;

export default function MobileNav(props) {
    return (
        <NavProvider>
            <MobileNavContent {...props} />
        </NavProvider>
    );
}

function MobileNavContent(props) {
    const { toggleTheme } = props;
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const {
        actions: { activateItem, closeNav, selectOrganization },
        state: {
            activeOrganization,
            currentRoute,
            isHidden,
            isSmall,
            menuItemsMain,
            menuItemsUser,
            menus,
            open,
            organizations,
        },
    } = useNavContext();
    const accountMenuItems = menuItemsUser.filter(
        (item) => item.title != "Logout",
    );
    const navMenus = menus.filter((menu) => menu.items.length > 0);
    const signOutItem = menuItemsUser.find((item) => item.title == "Logout");

    const handleNavItemClick = useCallback(
        (item) => () => {
            activateItem(item, { closeOnNavigate: true });
        },
        [activateItem],
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key == "Escape") {
                closeNav();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [closeNav, open]);

    if (!isSmall || isHidden) {
        return null;
    }

    return (
        <Box
            id={mobileNavDrawerId}
            component="nav"
            aria-label="Mobile navigation"
            aria-hidden={!open}
            data-testid="mobile-nav-drawer"
            sx={{
                position: "fixed",
                top: `${mobileNavHeaderHeight}px`,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 1300,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                backgroundColor: colors.mobileBackground,
                visibility: open ? "visible" : "hidden",
                pointerEvents: open ? "auto" : "none",
                clipPath: open ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 100% 0%)",
                transition: theme.transitions.create(
                    ["clip-path", "visibility"],
                    {
                        easing: theme.transitions.easing.easeOut,
                        duration: mobileNavTransitionDuration,
                    },
                ),
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    margin: "18px 0 0",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                    transform: open ? "translateY(0)" : "translateY(-40px)",
                    transition: theme.transitions.create(["transform"], {
                        easing: theme.transitions.easing.easeOut,
                        duration: open ? mobileNavTransitionDuration : 0,
                        delay: open ? 0 : mobileNavTransitionDuration,
                    }),
                }}
            >
                <Box
                    sx={{
                        margin: "0 18px",
                        paddingBottom: "18px",
                        overflow: "visible",
                        "@keyframes mobileNavSectionStagger": {
                            from: {
                                opacity: 0,
                                transform: "translateY(-15px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        "& > *:not(:last-child):nth-child(1)": {
                            animation: open
                                ? `mobileNavSectionStagger ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 0ms both`
                                : "none",
                        },
                        "& > *:not(:last-child):nth-child(2)": {
                            animation: open
                                ? `mobileNavSectionStagger ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 25ms both`
                                : "none",
                        },
                        "& > *:not(:last-child):nth-child(3)": {
                            animation: open
                                ? `mobileNavSectionStagger ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 50ms both`
                                : "none",
                        },
                        "& > *:not(:last-child):nth-child(4)": {
                            animation: open
                                ? `mobileNavSectionStagger ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 75ms both`
                                : "none",
                        },
                        "& > *:not(:last-child):nth-child(5)": {
                            animation: open
                                ? `mobileNavSectionStagger ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 100ms both`
                                : "none",
                        },
                    }}
                >
                    <OrganizationSwitcher.Mobile
                        activeOrganization={activeOrganization}
                        organizations={organizations}
                        onSelectOrganization={selectOrganization}
                    />

                    <Divider
                        sx={{
                            marginBottom: "24px",
                            borderColor:
                                theme.palette.mode == "dark"
                                    ? colorPalette.grey.dark.three
                                    : colorPalette.grey.light.three,
                        }}
                    />

                    <MobileNavPrimaryList>
                        {menuItemsMain.map((item) => (
                            <MemoMobileNavItem
                                key={item.title}
                                active={getRouteBase(item.url) == currentRoute}
                                item={item}
                                primary
                                onClick={handleNavItemClick(item)}
                            />
                        ))}
                    </MobileNavPrimaryList>

                    {(accountMenuItems.length > 0 || navMenus.length > 0) && (
                        <MobileNavSection title="Manage">
                            {accountMenuItems.map((item) => (
                                <MemoMobileNavItem
                                    key={item.title}
                                    active={
                                        getRouteBase(item.url) == currentRoute
                                    }
                                    item={item}
                                    primary
                                    onClick={handleNavItemClick(item)}
                                />
                            ))}
                            {navMenus.map((menu) => (
                                <MemoMobileNavGroup
                                    key={menu.title}
                                    currentRoute={currentRoute}
                                    menu={menu}
                                    primary
                                    onItemClick={handleNavItemClick}
                                />
                            ))}
                        </MobileNavSection>
                    )}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            margin: "41px 0 calc(18px + env(safe-area-inset-bottom))",
                            animation: open
                                ? `mobileNavFooterSlideIn ${mobileNavTransitionDuration}ms ${theme.transitions.easing.easeOut} 70ms both`
                                : "none",
                            "@keyframes mobileNavFooterSlideIn": {
                                from: {
                                    opacity: 0,
                                    transform: "translateX(28px)",
                                },
                                to: {
                                    opacity: 1,
                                    transform: "translateX(0)",
                                },
                            },
                        }}
                    >
                        {signOutItem && (
                            <MemoSignOutButton
                                onClick={handleNavItemClick(signOutItem)}
                            />
                        )}
                        <ThemeSwitch
                            checked={theme.palette.mode != "dark"}
                            onChange={toggleTheme}
                            inputProps={{ "aria-label": "Toggle theme" }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function MobileNavSection({ title, children }) {
    return (
        <Box>
            <Typography
                sx={{
                    height: 16,
                    display: "flex",
                    alignItems: "center",
                    color: colorPalette.universalGrey,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    margin: "0 0 10px 4px",
                }}
            >
                {title}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function MobileNavPrimaryList({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "26px",
            }}
        >
            {children}
        </Box>
    );
}

function MobileNavItem({
    active = false,
    item,
    onClick,
    nested = false,
    primary = false,
}) {
    const theme = useTheme();
    const textColor =
        theme.palette.mode == "dark" ? "#FFFFFF" : colorPalette.grey.dark.three;

    return (
        <ButtonBase
            disableRipple
            onClick={onClick}
            aria-current={active ? "page" : undefined}
            aria-label={item.title}
            data-testid={getNavItemTestId(item.title)}
            sx={{
                width: "100%",
                height: primary ? 23 : nested ? 32 : 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: primary ? "6px" : "10px",
                padding: primary ? "5px 6px" : nested ? "0 0 0 23px" : 0,
                boxSizing: "border-box",
                borderRadius: primary ? "5px" : "6px",
                color: primary
                    ? textColor
                    : theme.palette.mode == "dark"
                      ? colorPalette.grey.light.one
                      : colorPalette.grey.dark.two,
                opacity: primary ? 0.8 : 1,
                textAlign: "left",
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor:
                        theme.palette.mode == "dark"
                            ? colorPalette.grey.dark.three
                            : colorPalette.grey.light.three,
                },
                "&:active, &.Mui-focusVisible": {
                    backgroundColor: "transparent",
                },
            }}
        >
            <NavItemIcon icon={item.icon} size={13} />
            <Typography
                noWrap
                sx={{
                    color: "inherit",
                    display: primary ? "inline-flex" : "block",
                    alignItems: primary ? "center" : "normal",
                    height: primary ? 12 : "auto",
                    fontSize: primary ? 16 : nested ? 15 : 20,
                    fontWeight: primary ? 300 : 400,
                    fontFamily: theme.typography.fontFamily,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    maxWidth: primary ? "120px" : "none",
                    overflow: "visible",
                }}
            >
                {item.title}
            </Typography>
        </ButtonBase>
    );
}

function MobileNavGroup({ currentRoute, menu, onItemClick, primary = false }) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const title = menu.title;
    const textColor =
        theme.palette.mode == "dark" ? "#FFFFFF" : colorPalette.grey.dark.three;
    const dividerColor =
        theme.palette.mode == "dark"
            ? colorPalette.grey.dark.three
            : colorPalette.grey.light.three;
    const groupContentId = `${getNavItemTestId(menu.title)}-mobile-items`;

    return (
        <Box>
            <ButtonBase
                disableRipple
                onClick={() => setOpen((currentOpen) => !currentOpen)}
                aria-controls={groupContentId}
                aria-expanded={open}
                aria-label={title}
                data-testid={getNavItemTestId(menu.title)}
                sx={{
                    width: "100%",
                    height: primary ? 23 : 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: primary ? "5px 6px" : 0,
                    boxSizing: "border-box",
                    borderRadius: primary ? "5px" : "6px",
                    color: primary
                        ? textColor
                        : theme.palette.mode == "dark"
                          ? colorPalette.grey.light.one
                          : colorPalette.grey.dark.two,
                    opacity: primary ? 0.8 : 1,
                    backgroundColor: "transparent",
                    "&:hover": {
                        backgroundColor:
                            theme.palette.mode == "dark"
                                ? colorPalette.grey.dark.three
                                : colorPalette.grey.light.three,
                    },
                    "&:active, &.Mui-focusVisible": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: primary ? "6px" : "10px",
                        minWidth: 0,
                    }}
                >
                    <NavItemIcon icon={menu.icon} size={13} />
                    <Typography
                        noWrap
                        sx={{
                            color: "inherit",
                            display: primary ? "inline-flex" : "block",
                            alignItems: primary ? "center" : "normal",
                            height: primary ? 12 : "auto",
                            fontSize: primary ? 16 : 20,
                            fontWeight: primary ? 300 : 400,
                            fontFamily: theme.typography.fontFamily,
                            lineHeight: "100%",
                            letterSpacing: 0,
                            maxWidth: primary ? "120px" : "none",
                            overflow: "visible",
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <NavItemIcon
                    icon={AltArrowDown}
                    size={13}
                    sx={{
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transformOrigin: "center",
                        transition: theme.transitions.create("transform", {
                            easing: theme.transitions.easing.easeOut,
                            duration: mobileNavTransitionDuration,
                        }),
                    }}
                />
            </ButtonBase>
            <Collapse id={groupContentId} in={open} timeout={180} unmountOnExit>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        margin: "8px 0 0 9px",
                        padding: "0 0 0 10px",
                        borderLeft: `1px solid ${dividerColor}`,
                    }}
                >
                    {menu.items.map((item) => (
                        <MobileSupportSubMenuOption
                            key={item.title}
                            active={getRouteBase(item.url) == currentRoute}
                            item={item}
                            onClick={onItemClick(item)}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

function MobileSupportSubMenuOption({ active = false, item, onClick }) {
    const theme = useTheme();
    const textColor =
        theme.palette.mode == "dark" ? "#FFFFFF" : colorPalette.grey.dark.three;
    const hoverBackground =
        theme.palette.mode == "dark"
            ? colorPalette.grey.dark.three
            : colorPalette.grey.light.three;

    return (
        <ButtonBase
            disableRipple
            onClick={onClick}
            aria-current={active ? "page" : undefined}
            aria-label={item.title}
            data-testid={getNavItemTestId(item.title)}
            sx={{
                width: "100%",
                height: 23,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "5px 6px",
                boxSizing: "border-box",
                borderRadius: "5px",
                color: textColor,
                opacity: 0.8,
                textAlign: "left",
                backgroundColor: "transparent",
                "&:hover": {
                    backgroundColor: hoverBackground,
                },
                "&:active, &.Mui-focusVisible": {
                    backgroundColor: "transparent",
                },
            }}
        >
            <Typography
                noWrap
                sx={{
                    color: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 12,
                    fontSize: 16,
                    fontWeight: 300,
                    fontFamily: theme.typography.fontFamily,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    maxWidth: "120px",
                    overflow: "visible",
                }}
            >
                {item.title}
            </Typography>
        </ButtonBase>
    );
}

function SignOutButton({ onClick }) {
    return (
        <ButtonBase
            onClick={onClick}
            aria-label="Sign out"
            data-testid="mobile-sign-out-button"
            sx={{
                width: 88,
                height: 30,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                padding: "0 12px",
                borderRadius: "100px",
                backgroundColor: colorPalette.green.dark.one,
                color: "#FFFFFF",
                "&:hover": {
                    backgroundColor: colorPalette.green.dark.two,
                },
            }}
        >
            <Typography
                component="span"
                sx={{
                    color: "inherit",
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "100%",
                }}
            >
                Sign Out
            </Typography>
            <NavItemIcon icon={Logout2} size={14} />
        </ButtonBase>
    );
}

const MemoMobileNavGroup = memo(MobileNavGroup);
const MemoMobileNavItem = memo(MobileNavItem);
const MemoSignOutButton = memo(SignOutButton);
